import { useCallback } from "react";
import type { ChangeEvent } from "react";

import { useLocalization } from "@fluent/react";
import { TextInput } from "@narmi/design_system";
import cc from "classcat";
import { useNotificationContext } from "cerulean";

import {
  NOTE_MAX_LENGTH,
  useTransactionNote,
} from "../../hooks/useTransactionNote";
import PanelSectionHeading from "../TransactionDetailsPanel/PanelSectionHeading";

import styles from "./NotesPanelSection.module.scss";

import type { NoteSaveStatus } from "../../hooks/useTransactionNote";
import type { ReactLocalization } from "@fluent/react";

const statusLabel = (status: NoteSaveStatus, l10n: ReactLocalization) => {
  switch (status) {
    case "saving":
      return l10n.getString("transaction-note-saving");
    case "saved":
      return l10n.getString("transaction-note-saved");
    case "failed":
      return l10n.getString("live-ledger-panel-notes-failed");
    default:
      return null;
  }
};

interface NotesPanelSectionProps {
  transaction: API.Transaction;
  isPanelOpen: boolean;
}

const NotesPanelSection = ({
  transaction,
  isPanelOpen,
}: NotesPanelSectionProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  const onFailureOutOfView = useCallback(() => {
    sendNotification({
      type: "negative",
      text: l10n.getString("live-ledger-notes-save-error"),
    });
  }, [l10n, sendNotification]);

  const { value, status, onChange, onBlur } = useTransactionNote(transaction, {
    isPanelOpen,
    onFailureOutOfView,
  });

  const label = statusLabel(status, l10n);
  const failedText = l10n.getString("live-ledger-panel-notes-failed");

  return (
    <>
      <PanelSectionHeading
        title={l10n.getString("live-ledger-panel-notes-title")}
        accessory={
          label && (
            <span
              role="status"
              className={cc([
                "fontSize--s",
                status === "failed"
                  ? "fontColor--error"
                  : "fontColor--secondary",
              ])}
            >
              {label}
            </span>
          )
        }
      />
      <div className={styles.noteInput}>
        <TextInput
          multiline
          maxLength={NOTE_MAX_LENGTH}
          label={l10n.getString("live-ledger-panel-notes-placeholder")}
          value={value}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            onChange(event.target.value)
          }
          onBlur={onBlur}
          error={status === "failed" ? failedText : undefined}
          renderError={false}
          testId="panel-notes-input"
        />
      </div>
    </>
  );
};

export default NotesPanelSection;
